import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import Modal from '../Modal';
import CategoryAutocomplete from './CategoryAutocomplete';
import {
  generalExpensesApi,
  GeneralExpenseDto,
  ExpenseCategoryDto,
  ExpensePaymentMethodOptions,
  ExpensePaymentMethod,
} from '../../services/generalExpensesService';

interface ExpenseFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  expense?: GeneralExpenseDto | null; // עריכה אם קיים, אחרת יצירה
  categories: ExpenseCategoryDto[];
  onCategoriesChanged: () => void; // רענון רשימת הקטגוריות לאחר יצירה דינמית
}

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: var(--text-primary);
  font-weight: 500;
  font-size: 0.9rem;
`;

const Input = styled.input<{ $hasError?: boolean }>`
  padding: 0.75rem;
  border: 1px solid ${props => (props.$hasError ? 'var(--error-color)' : 'var(--border-color)')};
  border-radius: var(--border-radius-sm);
  font-size: 1rem;
  direction: rtl;
  text-align: right;

  &:focus {
    outline: none;
    border-color: ${props => (props.$hasError ? 'var(--error-color)' : 'var(--primary-color)')};
    box-shadow: 0 0 0 2px var(--primary-light);
  }
`;

const Select = styled.select<{ $hasError?: boolean }>`
  padding: 0.75rem;
  border: 1px solid ${props => (props.$hasError ? 'var(--error-color)' : 'var(--border-color)')};
  border-radius: var(--border-radius-sm);
  font-size: 1rem;
  direction: rtl;
  text-align: right;
  background: var(--surface-color);

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px var(--primary-light);
  }
`;

const Textarea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: 1rem;
  direction: rtl;
  text-align: right;
  resize: vertical;
  min-height: 70px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px var(--primary-light);
  }
`;

const ErrorMessage = styled.span`
  color: var(--error-color);
  font-size: 0.8rem;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 10px 20px;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.2s ease;

  ${props =>
    props.variant === 'primary'
      ? `background: var(--primary-color); color: #fff;
         &:hover:not(:disabled) { background: var(--primary-hover); }`
      : `background: var(--surface-color); color: var(--text-primary); border-color: var(--border-color);
         &:hover { background: var(--hover-background); }`}

  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const toDateInput = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

const todayInput = (): string => toDateInput(new Date().toISOString());

const ExpenseFormModal: React.FC<ExpenseFormModalProps> = ({
  isOpen,
  onClose,
  onSaved,
  expense,
  categories,
  onCategoriesChanged,
}) => {
  const isEdit = !!expense;

  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [categoryName, setCategoryName] = useState('');
  const [amount, setAmount] = useState('');
  const [expenseDate, setExpenseDate] = useState('');
  const [notes, setNotes] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<number>(ExpensePaymentMethod.Cash);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  // אתחול הטופס בכל פתיחה
  useEffect(() => {
    if (!isOpen) return;
    if (expense) {
      setCategoryId(expense.categoryId);
      setCategoryName(expense.categoryName);
      setAmount(String(expense.amount));
      setExpenseDate(toDateInput(expense.expenseDate));
      setNotes(expense.notes ?? '');
      setPaymentMethod(expense.paymentMethod);
    } else {
      setCategoryId(null);
      setCategoryName('');
      setAmount('');
      setExpenseDate(todayInput());
      setNotes('');
      setPaymentMethod(ExpensePaymentMethod.Cash);
    }
    setErrors({});
  }, [isOpen, expense]);

  const validateField = (name: string): string | null => {
    switch (name) {
      case 'category':
        if (!categoryId) return 'יש לבחור או להוסיף קטגוריה';
        return null;
      case 'amount': {
        if (!amount.trim()) return 'יש להזין סכום';
        const num = Number(amount);
        if (isNaN(num)) return 'הסכום אינו תקין';
        if (num <= 0) return 'הסכום חייב להיות גדול מאפס';
        return null;
      }
      case 'expenseDate':
        if (!expenseDate) return 'יש לבחור תאריך';
        return null;
      default:
        return null;
    }
  };

  const setFieldError = (name: string) => {
    const err = validateField(name);
    setErrors(prev => {
      const next = { ...prev };
      if (err) next[name] = err;
      else delete next[name];
      return next;
    });
  };

  const clearError = (name: string) => {
    setErrors(prev => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleCreateCategory = async (name: string): Promise<ExpenseCategoryDto> => {
    const created = await generalExpensesApi.createCategory(name);
    onCategoriesChanged();
    return created;
  };

  const validateAll = (): boolean => {
    const newErrors: Record<string, string> = {};
    ['category', 'amount', 'expenseDate'].forEach(f => {
      const err = validateField(f);
      if (err) newErrors[f] = err;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateAll() || !categoryId) return;

    const dto = {
      categoryId,
      amount: Number(amount),
      expenseDate,
      notes: notes.trim() || undefined,
      paymentMethod,
    };

    try {
      setSaving(true);
      if (isEdit && expense) {
        await generalExpensesApi.update(expense.id, dto);
        toast.success('ההוצאה עודכנה בהצלחה');
      } else {
        await generalExpensesApi.create(dto);
        toast.success('ההוצאה נוספה בהצלחה');
      }
      onSaved();
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data || 'אירעה שגיאה בשמירת ההוצאה');
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <>
      <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
        ביטול
      </Button>
      <Button type="submit" form="expense-form" variant="primary" disabled={saving}>
        {saving ? 'שומר...' : isEdit ? 'עדכון' : 'הוספה'}
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'עריכת הוצאה' : 'הוספת הוצאה חדשה'}
      width="480px"
      footer={footer}
    >
      <Form id="expense-form" onSubmit={handleSubmit}>
        <FormGroup>
          <Label>קטגוריה</Label>
          <CategoryAutocomplete
            categories={categories}
            selectedId={categoryId}
            selectedName={categoryName}
            onChange={(id, name) => {
              setCategoryId(id);
              setCategoryName(name);
              if (id) clearError('category');
            }}
            onCreateCategory={handleCreateCategory}
            onBlur={() => setFieldError('category')}
            hasError={!!errors.category}
          />
          {errors.category && <ErrorMessage>{errors.category}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label>סכום (₪)</Label>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={amount}
            $hasError={!!errors.amount}
            onChange={e => {
              setAmount(e.target.value);
              clearError('amount');
            }}
            onBlur={() => setFieldError('amount')}
            placeholder="0.00"
          />
          {errors.amount && <ErrorMessage>{errors.amount}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label>תאריך</Label>
          <Input
            type="date"
            value={expenseDate}
            $hasError={!!errors.expenseDate}
            onChange={e => {
              setExpenseDate(e.target.value);
              clearError('expenseDate');
            }}
            onBlur={() => setFieldError('expenseDate')}
          />
          {errors.expenseDate && <ErrorMessage>{errors.expenseDate}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label>אופן תשלום</Label>
          <Select
            value={paymentMethod}
            onChange={e => setPaymentMethod(Number(e.target.value))}
          >
            {ExpensePaymentMethodOptions.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>הערות</Label>
          <Textarea
            value={notes}
            maxLength={500}
            onChange={e => setNotes(e.target.value)}
            placeholder="הערות (אופציונלי)"
          />
        </FormGroup>
      </Form>
    </Modal>
  );
};

export default ExpenseFormModal;
